import type {
  CheckoutPreviewResponse,
  CourseDetailResponse,
  CourseListResponse,
  EnrollFreeResponse,
  PaymentOrderResponse,
  PricingCheckoutResponse,
  PricingEmailOtpResponse,
  PricingEmailOtpVerifyResponse,
  PricingOtpResponse,
  PricingOtpVerifyResponse,
  PricingWhatsappNumberValidationResponse,
  StudentAuthResponse,
  StudentDashboardResponse,
  StudentMeResponse,
  StudentPlayerResponse,
  UpdateProgressPayload,
  UpdateProgressResponse,
  VerifyPaymentResponse,
} from './types';
import { getCmsBackendUrl } from './utils';

interface JsonErrorResponse {
  message?: string;
  details?: unknown;
}

export class LmsApiError extends Error {
  status: number;
  details?: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = 'LmsApiError';
    this.status = status;
    this.details = details;
  }
}

const withQuery = (path: string, query?: Record<string, unknown>) => {
  const url = new URL(path.startsWith('/') ? path : `/${path}`, getCmsBackendUrl());

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }

      url.searchParams.set(key, String(value));
    });
  }

  return url.toString();
};

const request = async <T>(
  path: string,
  options: RequestInit & {
    token?: string;
    query?: Record<string, unknown>;
  } = {},
) => {
  const { token, query, headers, body, ...rest } = options;
  const requestHeaders = new Headers(headers);

  if (token) {
    requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  if (!(body instanceof FormData) && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  const response = await fetch(withQuery(path, query), {
    ...rest,
    headers: requestHeaders,
    body,
    cache: 'no-store',
  });

  if (!response.ok) {
    let payload: JsonErrorResponse | null = null;

    try {
      payload = (await response.json()) as JsonErrorResponse;
    } catch {
      payload = null;
    }

    throw new LmsApiError(response.status, payload?.message || 'Request failed.', payload?.details);
  }

  return (await response.json()) as T;
};

export const signupStudent = (payload: { name: string; email: string; phone: string; password: string }) =>
  request<StudentAuthResponse>('/api/auth/signup', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const loginStudent = (payload: { email: string; password: string }) =>
  request<StudentAuthResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const startPricingCheckout = (
  payload: {
    resumeToken?: string;
    planSnapshot?: {
      planId: string;
      planName: string;
      category?: string;
      billingTab: string;
      priceLabel?: string;
      priceSuffix?: string;
      billingNote?: string;
    };
    selectedDuration?: string;
    selectedAddons?: Array<{ label: string; price?: string; amount?: number }>;
    estimatedAmount?: number;
  },
  token?: string,
) =>
  request<PricingCheckoutResponse>('/api/pricing-checkouts/start', {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const resumePricingCheckout = (resumeToken: string, token?: string) =>
  request<PricingCheckoutResponse>(`/api/pricing-checkouts/resume/${resumeToken}`, {
    token,
  });

export const updatePricingCheckoutSelection = (
  checkoutId: string,
  payload: {
    selectedDuration: string;
    selectedAddons: Array<{ label: string; price?: string; amount?: number }>;
    estimatedAmount: number;
  },
  token?: string,
) =>
  request<PricingCheckoutResponse>(`/api/pricing-checkouts/${checkoutId}/selection`, {
    token,
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const updatePricingCheckoutDetails = (
  checkoutId: string,
  payload: {
    fullName: string;
    brandName: string;
    city: string;
    email: string;
    whatsappNumber: string;
    callNumber?: string;
    gstRegistered: boolean;
    gstNumber?: string;
    aboutBusiness: string;
    requirementNotes?: string;
  },
  token?: string,
) =>
  request<PricingCheckoutResponse>(`/api/pricing-checkouts/${checkoutId}/details`, {
    token,
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const sendPricingWhatsappOtp = (
  checkoutId: string,
  payload: { whatsappNumber: string; fullName?: string },
  token?: string,
) =>
  request<PricingOtpResponse>(`/api/pricing-checkouts/${checkoutId}/whatsapp-otp/send`, {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const sendPricingEmailOtp = (
  checkoutId: string,
  payload: { email: string; fullName?: string },
  token?: string,
) =>
  request<PricingEmailOtpResponse>(`/api/pricing-checkouts/${checkoutId}/email-otp/send`, {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const validatePricingWhatsappNumber = (payload: { whatsappNumber: string }, token?: string) =>
  request<PricingWhatsappNumberValidationResponse>('/api/pricing-checkouts/whatsapp-number/validate', {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const verifyPricingWhatsappOtp = (checkoutId: string, payload: { otp: string }, token?: string) =>
  request<PricingOtpVerifyResponse>(`/api/pricing-checkouts/${checkoutId}/whatsapp-otp/verify`, {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const verifyPricingEmailOtp = (checkoutId: string, payload: { otp: string }, token?: string) =>
  request<PricingEmailOtpVerifyResponse>(`/api/pricing-checkouts/${checkoutId}/email-otp/verify`, {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const submitPricingCheckout = (checkoutId: string, token?: string) =>
  request<PricingCheckoutResponse>(`/api/pricing-checkouts/${checkoutId}/submit`, {
    token,
    method: 'POST',
  });

export const abandonPricingCheckout = (checkoutId: string, token?: string) =>
  request<PricingCheckoutResponse>(`/api/pricing-checkouts/${checkoutId}/abandon`, {
    token,
    method: 'POST',
  });

export const getStudentMe = (token: string) =>
  request<StudentMeResponse>('/api/auth/me', {
    token,
  });

export const getPublicCourses = (query?: Record<string, unknown>, token?: string) =>
  request<CourseListResponse>('/api/public/courses', {
    token,
    query,
  });

export const getPublicCourse = (slug: string, token?: string) =>
  request<CourseDetailResponse>(`/api/public/courses/${slug}`, {
    token,
  });

export const enrollInFreeCourse = (slug: string, token: string) =>
  request<EnrollFreeResponse>(`/api/public/courses/${slug}/enroll-free`, {
    token,
    method: 'POST',
  });

export const previewCheckout = (slug: string, token: string, couponCode?: string) =>
  request<CheckoutPreviewResponse>(`/api/public/courses/${slug}/checkout-preview`, {
    token,
    method: 'POST',
    body: JSON.stringify({
      ...(couponCode ? { couponCode } : {}),
    }),
  });

export const createPaymentOrder = (slug: string, token: string, couponCode?: string) =>
  request<PaymentOrderResponse>(`/api/payments/checkout/${slug}`, {
    token,
    method: 'POST',
    body: JSON.stringify({
      ...(couponCode ? { couponCode } : {}),
    }),
  });

export const verifyPaymentOrder = (
  token: string,
  payload: {
    paymentId: string;
    razorpayOrderId: string;
    razorpayPaymentId: string;
    razorpaySignature: string;
  },
) =>
  request<VerifyPaymentResponse>('/api/payments/verify', {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const getLessonPlayer = (courseSlug: string, lessonSlug: string, token: string) =>
  request<StudentPlayerResponse>(`/api/student/learn/${courseSlug}/${lessonSlug}`, {
    token,
  });

export const getStudentDashboard = (token: string) =>
  request<StudentDashboardResponse>('/api/student/dashboard', {
    token,
  });

export const updateLessonProgress = (lessonId: string, token: string, payload: UpdateProgressPayload) =>
  request<UpdateProgressResponse>(`/api/student/lessons/${lessonId}/progress`, {
    token,
    method: 'POST',
    body: JSON.stringify(payload),
  });
